import useApi from 'hooks/useApi';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  getActiveDetail,
  saveDocuments,
  setCurrentNotificationReview,
  setCurrentNotificationReviewId,
  saveAnswers,
  getAdvisors,
} from 'redux/actions/notificationReviewActions';

const useNotification = () => {
  const { customAreaApi } = useApi();
  const history = useHistory();
  const dispatch = useDispatch();
  const { cusNavId, isCustomer, mail, clientId, admins } = useSelector((state) => state.auth);
  const { searchValue } = useSelector((state) => state.topBar);
  const { showTypeNotifications } = useSelector((state) => state.notification);
  const { currentClient } = useSelector((state) => state.clientState);
  const { currentNotificationReviewId } = useSelector((state) => state.notificationReview);

  const [{ loading: massivesLoading }, getMassiveNotifications] = customAreaApi(
    { method: 'POST' },
    { manual: true }
  );

  const [{ loading: individualsLoading }, getIndividualNotifications] = customAreaApi(
    { method: 'POST' },
    { manual: true }
  );

  const [{ loading: individualNotificationLoading }, getIndividualNotificationById] = customAreaApi(
    { method: 'GET' },
    { manual: true }
  );

  const [{ loading: massiveNotificationLoading }, getMassiveNotificationById] = customAreaApi(
    { method: 'GET' },
    { manual: true }
  );

  const [{ loading: markIndividualAsReadLoading }, markIndividualAsRead] = customAreaApi(
    { method: 'POST' },
    { manual: true }
  );

  const [{ loading: markMassiveAsReadLoading }, markMassiveAsRead] = customAreaApi(
    { method: 'POST' },
    { manual: true }
  );

  const getMassives = async ({ filters = [], limit = 0, skip = 0 }) => {
    try {
      const { data } = await getMassiveNotifications({
        url: `${window.API_V1}/gen/not/${cusNavId}/search?login=${mail}`,
        data: {
          filters,
          reduce: {
            limit,
            skip,
          },
        },
      });
      return data;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const getAllMassives = async ({ filters = [], limit = 0, skip = 0 }) => {
    filters.push({
      model: 'GeneralNotify',
      field: 'Date',
      asc: false,
    });
    if (!isCustomer) {
      filters.push({
        model: 'GeneralNotify',
        field: 'Mail',
        filter: [mail],
      });
      filters.push({
        model: 'GeneralNotify',
        field: 'ClientId',
        filter: [clientId],
      });
    }
    const { data } = await getMassives({ filters, limit, skip });
    return data;
  };

  const getIndividuals = async ({ filters = [], limit = 0, skip = 0 }) => {
    try {
      const { data } = await getIndividualNotifications({
        url: `${window.API_V1}/cli/notifications/${cusNavId}/search?login=${mail}`,
        data: {
          filters,
          reduce: {
            limit,
            skip,
          },
        },
      });
      return data;
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const getAllIndividuals = async ({ filters = [], limit = 0, skip = 0 }) => {
    filters.push({
      model: 'Notify',
      field: 'Date',
      asc: false,
    });

    if (searchValue) {
      filters.push({
        model: 'Notify',
        field: 'ClientId',
        filter: [searchValue],
      });
    }

    if (!isCustomer) {
      filters.push(
        {
          model: 'Notify',
          field: 'ClientId',
          filter: [clientId],
        },
        {
          model: 'Notify',
          field: 'Mail',
          filter: [mail],
        }
      );
    }
    const { data } = await getIndividuals({ filters, limit, skip });
    return data;
  };

  const getIndividualById = async ({ currentClientId, notificationId }) => {
    try {
      const { data } = await getIndividualNotificationById({
        url: `${window.API_V1}/cli/notifications/${cusNavId}/${currentClientId}/get/${notificationId}?login=${mail}`,
      });
      return data.data;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const getMassiveById = async (notificationId) => {
    try {
      const { data } = await getMassiveNotificationById({
        url: `${window.API_V1}/gen/not/${cusNavId}/get/${notificationId}?login=${mail}`,
      });
      return data.data;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const getContacts = (linkToContact, responsibles, createdBy) => {
    if (isCustomer) {
      const existAdvisor = responsibles.some((responsibleMail) => responsibleMail === createdBy);
      if (!existAdvisor) {
        responsibles.push(createdBy);
      }
    }
    const setNotificationContacts = linkToContact.map((contactMail) => {
      return {
        fullName: contactMail.name,
        mail: contactMail.mail,
        isInCharge: false,
        active: contactMail.active,
        clientName: contactMail.clientName || null,
      };
    });
    const setNotificationContactsResponsibles = responsibles.map((responsibleMail) => {
      const adminFind = admins.find((admin) => responsibleMail === admin.mail);
      if (responsibleMail === createdBy && adminFind) {
        return {
          fullName: adminFind?.nameComplete,
          mail: adminFind.mail,
          isInCharge: true,
          active: adminFind.active,
        };
      }
      return {
        fullName: adminFind?.nameComplete,
        mail: adminFind?.mail,
        isInCharge: true,
        active: adminFind?.active,
      };
    });
    dispatch(getAdvisors([...setNotificationContactsResponsibles]));
    return [...setNotificationContacts, ...setNotificationContactsResponsibles];
  };

  const getAnswer = async (answers) => {
    let newAnswers = [];
    for (let index = 0; index < answers.length; index += 1) {
      const answer = answers[index];
      if (answer.docs !== undefined) {
        for (let i = 0; i < answer.docs.length; i += 1) {
          const document = answer.docs[i];
          if (document.type.toLowerCase() === '.png' || document.type.toLowerCase() === '.jpg') {
            document.isLoadingImg = true;
          }
        }
      }
      newAnswers = [...newAnswers, { ...answer }];
    }
    dispatch(saveAnswers(newAnswers));
  };

  const getNotificationById = async (id) => {
    let notificationData;
    if (showTypeNotifications) {
      notificationData = await getMassiveById(id);
    } else {
      notificationData = await getIndividualById({
        notificationId: id,
        currentClientId: currentClient?.clientId || clientId,
      });
    }
    return notificationData;
  };

  const handleSelectNotificationReview = (notification) => {
    try {
      const notificationReview = notification;

      const today = new Date();

      if (!notificationReview || !(today >= new Date(notificationReview?.date))) {
        dispatch(setCurrentNotificationReview(null));
        dispatch(setCurrentNotificationReviewId(''));
        return;
      }

      if (!showTypeNotifications) {
        if (notificationReview?.linkToContact && notificationReview?.responsibles) {
          notificationReview.participants = getContacts(
            notificationReview.linkToContact,
            notificationReview.responsibles,
            notificationReview.createdBy
          );
        }
      }

      if (showTypeNotifications) {
        if (notificationReview?.clientsAndContacts && notificationReview?.responsibles) {
          const clientsContacts = notificationReview.clientsAndContacts.flatMap((client) => {
            const contacts = client.linkToContacts.map((contact) => {
              return { clientName: client.clientName, ...contact };
            });
            return contacts;
          });
          notificationReview.participants = getContacts(
            clientsContacts,
            notificationReview.responsibles,
            notificationReview.createdBy
          );
        }
      }
      const answers = notificationReview.answers.map((answer) => {
        return { ...answer, isBD: true, isNew: false };
      });
      if (notificationReview.docs) {
        const documents = notificationReview.docs.map((document) => {
          return { ...document, isBD: true };
        });
        dispatch(saveDocuments(documents));
      }
      getAnswer(answers);
      dispatch(setCurrentNotificationReview(notificationReview));
      dispatch(setCurrentNotificationReviewId(notificationReview.id));
      dispatch(getActiveDetail(null));
    } catch (error) {
      if (error.response) {
        dispatch(getActiveDetail(null));
        history.push('/home');
      }
    }
  };

  const refreshCurrentNotificationReview = async () => {
    const notificationReview = await getNotificationById(currentNotificationReviewId);
    handleSelectNotificationReview(notificationReview);
  };

  const individualAsRead = async ({ individualClientId, individualNotificationId }) => {
    try {
      await markIndividualAsRead({
        url: `${window.API_V1}/cli/notifications/${cusNavId}/${individualClientId}/${mail}/${individualNotificationId}/MarkAsRead`,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const massiveAsRead = async ({ massiveNotificationId }) => {
    try {
      await markMassiveAsRead({
        url: `${window.API_V1}/gen/not/${cusNavId}/${mail}/${massiveNotificationId}/MarkAsRead`,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const markAsRead = async (notificationId) => {
    // individuals

    if (!showTypeNotifications && !isCustomer) {
      await individualAsRead({
        individualClientId: clientId,
        individualNotificationId: notificationId,
      });
    }

    if (!showTypeNotifications && currentClient) {
      await individualAsRead({
        individualClientId: currentClient.clientId,
        individualNotificationId: notificationId,
      });
    }

    // massives
    if (showTypeNotifications) {
      await massiveAsRead({
        massiveNotificationId: notificationId,
      });
    }
  };

  return {
    massivesLoading,
    individualsLoading,
    getAllMassives,
    getAllIndividuals,
    individualNotificationLoading,
    getIndividualById,
    massiveNotificationLoading,
    getMassiveById,
    getNotificationById,
    handleSelectNotificationReview,
    refreshCurrentNotificationReview,
    markAsReadLoading: markIndividualAsReadLoading || markMassiveAsReadLoading,
    markAsRead,
  };
};

export default useNotification;
